import { lazy, Suspense, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import { trackTenantPageView } from "@/api/analytics";
import { fetchHomeBootstrap } from "@/api/content";
import { AppShell } from "@/components/app-shell";
import { LoadingState } from "@/components/ui/loading-state";
import { getCreatorOnboardingProgress } from "@/lib/creator-onboarding";
import { useAuthStore } from "@/store/auth-store";

const ROUTE_CHUNK_RETRY_PREFIX = "fanvault-route-chunk-retry:";
const GLOBAL_CHUNK_RETRY_KEY = "fanvault-global-chunk-retry";

function shouldReloadForChunkError(reason: unknown) {
  const message = reason instanceof Error ? reason.message : String(reason ?? "");

  return /Failed to fetch dynamically imported module/i.test(message)
    || /Importing a module script failed/i.test(message)
    || /Loading chunk [\w-]+ failed/i.test(message)
    || /ChunkLoadError/i.test(message);
}

function reloadOnceForChunkError() {
  if (typeof window === "undefined") {
    return false;
  }

  const retryKey = `${GLOBAL_CHUNK_RETRY_KEY}:${window.location.pathname}`;
  if (window.sessionStorage.getItem(retryKey) === "1") {
    window.sessionStorage.removeItem(retryKey);
    return false;
  }

  window.sessionStorage.setItem(retryKey, "1");
  window.location.reload();
  return true;
}

function lazyWithReload<T extends { default: React.ComponentType<any> }>(key: string, loader: () => Promise<T>) {
  return lazy(async () => {
    const retryKey = `${ROUTE_CHUNK_RETRY_PREFIX}${key}`;

    try {
      const module = await loader();
      if (typeof window !== "undefined") {
        window.sessionStorage.removeItem(retryKey);
      }
      return module;
    } catch (error) {
      if (typeof window !== "undefined") {
        const hasRetried = window.sessionStorage.getItem(retryKey) === "1";
        if (!hasRetried) {
          window.sessionStorage.setItem(retryKey, "1");
          window.location.reload();
          return new Promise<never>(() => {});
        }
        window.sessionStorage.removeItem(retryKey);
      }

      throw error;
    }
  });
}

const SplashPage = lazyWithReload("splash-page", () => import("@/pages/public/splash-page"));
const LandingPage = lazyWithReload("landing-page", () => import("@/pages/public/landing-page"));
const TenantLandingPage = lazyWithReload("tenant-landing-page", () => import("@/pages/public/tenant-landing-page"));
const CreatorsPage = lazyWithReload("creators-page", () => import("@/pages/public/creators-page"));
const LoginPage = lazyWithReload("login-page", () => import("@/pages/public/login-page"));
const RegisterPage = lazyWithReload("register-page", () => import("@/pages/public/register-page"));
const RequestResetPage = lazyWithReload("request-reset-page", () => import("@/pages/public/request-reset-page"));
const ResetPage = lazyWithReload("reset-page", () => import("@/pages/public/reset-page"));
const SubmitEmailPage = lazyWithReload("submit-email-page", () => import("@/pages/public/submit-email-page"));
const QuickSetupPage = lazyWithReload("quick-setup-page", () => import("@/pages/public/quick-setup-page"));
const UnknownPage = lazyWithReload("unknown-page", () => import("@/pages/public/unknown-page"));
const HomePage = lazyWithReload("home-page", () => import("@/pages/root/home-page"));
const DashboardPage = lazyWithReload("dashboard-page", () => import("@/pages/root/dashboard-page"));
const MarketingPage = lazyWithReload("marketing-page", () => import("@/pages/root/marketing-page"));
const OrdersPage = lazyWithReload("orders-page", () => import("@/pages/root/orders-page"));
const MembersPage = lazyWithReload("members-page", () => import("@/pages/root/members-page"));
const MembershipsPage = lazyWithReload("memberships-page", () => import("@/pages/root/memberships-page"));
const FoldersPage = lazyWithReload("folders-page", () => import("@/pages/root/folders-page"));
const EventsPage = lazyWithReload("events-page", () => import("@/pages/root/events-page"));
const EventDetailsPage = lazyWithReload("event-details-page", () => import("@/pages/root/event-details-page"));
const EventStreamPage = lazyWithReload("event-stream-page", () => import("@/pages/root/event-stream-page"));
const PostsPage = lazyWithReload("posts-page", () => import("@/pages/root/posts-page"));
const BookmarksPage = lazyWithReload("bookmarks-page", () => import("@/pages/root/bookmarks-page"));
const NotificationsPage = lazyWithReload("notifications-page", () => import("@/pages/root/notifications-page"));
const ProfilePage = lazyWithReload("profile-page", () => import("@/pages/root/profile-page"));
const SettingsPage = lazyWithReload("settings-page", () => import("@/pages/root/settings-page"));
const PayoutsPage = lazyWithReload("payouts-page", () => import("@/pages/root/payouts-page"));
const SubscriptionPage = lazyWithReload("subscription-page", () => import("@/pages/root/subscription-page"));
const CommunityPage = lazyWithReload("community-page", () => import("@/pages/root/community-page"));
const CommunityThreadPage = lazyWithReload("community-thread-page", () => import("@/pages/root/community-thread-page"));
const TenantPage = lazyWithReload("tenant-page", () => import("@/pages/root/tenant-page"));
const AccountSecurityPage = lazyWithReload("account-security-page", () => import("@/pages/root/account-security-page"));
const CreatorOnboardingPage = lazyWithReload("creator-onboarding-page", () => import("@/pages/root/creator-onboarding-page"));
const OnboardingWizardPage = lazyWithReload("onboarding-wizard-page", () => import("@/pages/root/onboarding-wizard-page"));

function LoadingRoute() {
  return <LoadingState className="min-h-[50vh] border-0 bg-transparent" label="Loading route..." />;
}

function ChunkRecovery() {
  useEffect(() => {
    function handleUnhandledRejection(event: PromiseRejectionEvent) {
      if (shouldReloadForChunkError(event.reason) && reloadOnceForChunkError()) {
        event.preventDefault();
      }
    }

    function handleError(event: ErrorEvent) {
      if (shouldReloadForChunkError(event.error ?? event.message)) {
        reloadOnceForChunkError();
      }
    }

    window.addEventListener("unhandledrejection", handleUnhandledRejection);
    window.addEventListener("error", handleError);

    return () => {
      window.removeEventListener("unhandledrejection", handleUnhandledRejection);
      window.removeEventListener("error", handleError);
    };
  }, []);

  return null;
}

function TenantPageAnalyticsTracker() {
  const location = useLocation();
  const tenantContext = useAuthStore((state) => state.tenantContext);
  const invalidTenantPath = useAuthStore((state) => state.invalidTenantPath);

  useEffect(() => {
    if (!tenantContext.subdomain || invalidTenantPath) {
      return;
    }

    const tenantPrefix = `/t/${tenantContext.subdomain}`;
    const normalizedPath = location.pathname.startsWith(tenantPrefix)
      ? location.pathname.slice(tenantPrefix.length) || "/"
      : location.pathname;

    const path = normalizedPath.startsWith("/") ? normalizedPath : `/${normalizedPath}`;
    const firstSegment = path.split("/").filter(Boolean)[0] ?? "landing";
    const pageName = firstSegment.replace(/-/g, " ");

    void trackTenantPageView(tenantContext.subdomain, path, pageName);
  }, [invalidTenantPath, location.pathname, tenantContext.subdomain]);

  return null;
}

function StartGate() {
  const { started, loading, start } = useAuthStore();

  useEffect(() => {
    void start();
  }, [start]);

  if (!started || loading) {
    return <LoadingRoute />;
  }

  return <Outlet />;
}

function TenantSync() {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!params.tenant) {
      return;
    }

    if (location.pathname === `/t/${params.tenant}`) {
      navigate(`/t/${params.tenant}/home`, { replace: true });
    }
  }, [location.pathname, navigate, params.tenant]);

  return <Outlet />;
}

function RequireKnownTenant() {
  const auth = useAuthStore();

  return auth.invalidTenantPath ? <Navigate replace to="/landing" /> : <Outlet />;
}

function RequireAuth() {
  const auth = useAuthStore();
  const location = useLocation();

  if (!auth.isAuthenticated()) {
    return <Navigate replace state={{ from: location.pathname }} to={auth.isCentralDomain() ? "/landing" : auth.resolveAppPath("/login")} />;
  }

  if (auth.user?.isFirstLogin && auth.isAdmin()) {
    return <Navigate replace to={auth.resolveAppPath("/quick-setup")} />;
  }

  return <Outlet />;
}

function RedirectIfAuthed() {
  const auth = useAuthStore();
  if (!auth.isAuthenticated()) {
    return <Outlet />;
  }

  if (auth.isCentralDomain() && !auth.isPlatformAdmin()) {
    return <Navigate replace to="/create-vault" />;
  }

  return <Navigate replace to={auth.resolveAppPath("/home")} />;
}

function RequireCentral() {
  const auth = useAuthStore();
  return auth.isCentralDomain() ? <Outlet /> : <Navigate replace to={auth.isAuthenticated() ? auth.resolveAppPath("/home") : auth.resolveAppPath("/login")} />;
}

function RequireCentralPlatformAdmin() {
  const auth = useAuthStore();
  return auth.isCentralDomain() && auth.isPlatformAdmin() ? <Outlet /> : <Navigate replace to={auth.isAuthenticated() ? "/create-vault" : "/login"} />;
}

function RequireSubdomain() {
  const auth = useAuthStore();
  return !auth.isCentralDomain() ? <Outlet /> : <Navigate replace to={auth.isPlatformAdmin() ? "/tenant" : "/create-vault"} />;
}

function RequireAdmin() {
  const auth = useAuthStore();
  return auth.isAdmin() ? <Outlet /> : <Navigate replace to={auth.resolveAppPath("/home")} />;
}

function RequireCentralOrAdmin() {
  const auth = useAuthStore();
  return auth.isCentralDomain() || auth.isAdmin() ? <Outlet /> : <Navigate replace to={auth.resolveAppPath("/home")} />;
}

function RequireCommunityEnabled() {
  const auth = useAuthStore();
  return auth.siteSettings?.hasForum ? <Outlet /> : <Navigate replace to={auth.resolveAppPath("/home")} />;
}

function RequireCreatorOnboardingCompleted() {
  const auth = useAuthStore();
  const location = useLocation();
  const onboardingPath = auth.resolveAppPath("/onboarding-wizard");
  const homePath = auth.resolveAppPath("/home");
  const shouldGate = auth.isAuthenticated() && !auth.isCentralDomain() && auth.isAdmin();

  const onboardingQuery = useQuery({
    queryKey: ["creator-onboarding", "gate"],
    queryFn: fetchHomeBootstrap,
    enabled: shouldGate,
    staleTime: 10_000,
  });

  if (!shouldGate) {
    return <Outlet />;
  }

  if (onboardingQuery.isLoading) {
    return <LoadingRoute />;
  }

  if (onboardingQuery.isError) {
    // Fail-open if the guard query fails to avoid trapping admins in a hard redirect loop.
    return <Outlet />;
  }

  const progress = getCreatorOnboardingProgress(onboardingQuery.data);
  const isOnboardingRoute = location.pathname === onboardingPath;
  const allowedSetupPaths = [
    onboardingPath,
    auth.resolveAppPath("/quick-setup"),
    auth.resolveAppPath("/create/video"),
    auth.resolveAppPath("/memberships"),
    auth.resolveAppPath("/memberships/create"),
    auth.resolveAppPath("/settings"),
  ];
  const isAllowedSetupRoute = allowedSetupPaths.includes(location.pathname)
    || location.pathname.startsWith(`${auth.resolveAppPath("/memberships")}/`);

  if (!progress.isComplete && !isAllowedSetupRoute) {
    return <Navigate replace to={onboardingPath} />;
  }

  if (progress.isComplete && isOnboardingRoute) {
    return <Navigate replace to={homePath} />;
  }

  return <Outlet />;
}

function PublicLayout() {
  return <div className="page-frame min-h-screen px-4 py-6 md:px-6 md:py-8"><Outlet /></div>;
}

function renderPrivateRoutes(tenant = false) {
  const path = (value: string) => (tenant ? value.replace(/^\//, "") : value);

  return (
    <>
      <Route path={path("/")} element={<Navigate replace to={path("/home")} />} />
      <Route path={path("/quick-setup")} element={<QuickSetupPage />} />
      <Route path={path("/onboarding-wizard")} element={<OnboardingWizardPage />} />
      {!tenant ? (
        <Route element={<RequireCentral />}>
          <Route path="/create-vault" element={<CreatorOnboardingPage />} />
        </Route>
      ) : null}
      <Route element={<RequireSubdomain />}>
        <Route path={path("/home")} element={<HomePage />} />
        <Route path={path("/profile")} element={<ProfilePage />} />
        <Route path={path("/notifications")} element={<NotificationsPage />} />
        <Route path={path("/subscription")} element={<SubscriptionPage />} />
        <Route element={<RequireCommunityEnabled />}>
          <Route path={path("/community")} element={<CommunityPage />} />
          <Route path={path("/community/:threadId")} element={<CommunityThreadPage />} />
        </Route>
        <Route path={path("/bookmarks")} element={<BookmarksPage />} />
        <Route path={path("/posts")}>
          <Route index element={<PostsPage />} />
          <Route path="folder/:folderId" element={<PostsPage />} />
          <Route path=":postId" element={<PostsPage />} />
          <Route path="edit/:postId" element={<PostsPage />} />
        </Route>
        <Route path={path("/events")} element={<EventsPage />} />
        <Route path={path("/events/create")} element={<EventsPage />} />
        <Route path={path("/events/:eventId")} element={<EventDetailsPage />} />
        <Route path={path("/events/edit/:eventId")} element={<EventsPage />} />
        <Route path={path("/events/stream/:eventId")} element={<EventStreamPage />} />
        <Route path={path("/password/change")} element={<AccountSecurityPage />} />
        <Route path={path("/email/change")} element={<AccountSecurityPage />} />
        <Route path={path("/terminate-account")} element={<AccountSecurityPage />} />
      </Route>
      <Route element={<RequireAdmin />}>
        <Route path={path("/dashboard")} element={<DashboardPage />} />
        <Route path={path("/members")} element={<MembersPage />} />
        <Route path={path("/marketing")} element={<MarketingPage />} />
        <Route path={path("/memberships")} element={<MembershipsPage />} />
        <Route path={path("/orders")} element={<OrdersPage />} />
        <Route path={path("/settings")} element={<SettingsPage />} />
        <Route path={path("/folders")} element={<FoldersPage />} />
        <Route path={path("/folders/create")} element={<FoldersPage />} />
        <Route path={path("/folders/:folderId")} element={<FoldersPage />} />
        <Route path={path("/memberships/create")} element={<MembershipsPage />} />
        <Route path={path("/memberships/:membershipId")} element={<MembershipsPage />} />
        <Route path={path("/create/:typeName")} element={<PostsPage />} />
      </Route>
      <Route element={<RequireCentralOrAdmin />}>
        <Route path={path("/payouts")} element={<PayoutsPage />} />
      </Route>
      {!tenant ? (
        <Route element={<RequireCentralPlatformAdmin />}>
          <Route path="/tenant" element={<TenantPage />} />
        </Route>
      ) : null}
    </>
  );
}

export function AppRouter() {
  return (
    <BrowserRouter>
      <ChunkRecovery />
      <TenantPageAnalyticsTracker />
      <Suspense fallback={<LoadingRoute />}>
        <Routes>
          <Route element={<StartGate />}>
            <Route element={<PublicLayout />}>
              <Route path="/splash" element={<SplashPage />} />
              <Route element={<RequireCentral />}>
                <Route path="/landing" element={<LandingPage />} />
                <Route path="/creators" element={<CreatorsPage />} />
              </Route>
              <Route element={<RedirectIfAuthed />}>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
              </Route>
              <Route path="/request-reset-password" element={<RequestResetPage />} />
              <Route path="/reset-password/:token" element={<ResetPage />} />
              <Route path="/change-email/:token" element={<SubmitEmailPage />} />
            </Route>

            <Route path="/t/:tenant" element={<TenantSync />}>
              <Route element={<RequireKnownTenant />}>
                <Route element={<PublicLayout />}>
                  <Route path="landing" element={<TenantLandingPage />} />
                  <Route element={<RedirectIfAuthed />}>
                    <Route path="login" element={<LoginPage />} />
                    <Route path="register" element={<RegisterPage />} />
                  </Route>
                  <Route path="request-reset-password" element={<RequestResetPage />} />
                  <Route path="reset-password/:token" element={<ResetPage />} />
                  <Route path="change-email/:token" element={<SubmitEmailPage />} />
                </Route>
                <Route element={<RequireAuth />}>
                  <Route element={<AppShell />}>
                    <Route element={<RequireCreatorOnboardingCompleted />}>
                      {renderPrivateRoutes(true)}
                    </Route>
                  </Route>
                </Route>
              </Route>
            </Route>

            <Route element={<RequireAuth />}>
              <Route element={<AppShell />}>
                {renderPrivateRoutes()}
              </Route>
            </Route>

            <Route path="*" element={<UnknownPage />} />
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
